import React from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';

import {Button} from 'primereact/button';
import {
    InputSwitch,
    InputSwitchChangeEvent
} from 'primereact/inputswitch';
import {InputText} from 'primereact/inputtext';
import {Panel} from 'primereact/panel';

import {StoreState} from '~/frontend/src/store';
import {
    hideMemoryConsumption,
    showMemoryConsumption,
    enableFormDebugAll,
    disableFormDebugAll,
    addDedicatedFormDebug,
    removeDedicatedFormDebug,
    enableDedicatedFormDebug,
    disableDedicatedFormDebug,
    enableFormDesignerDebugAll,
    disableFormDesignerDebugAll,
    addDedicatedFormDesignerDebug,
    removeDedicatedFormDesignerDebug,
    enableDedicatedFormDesignerDebug,
    disableDedicatedFormDesignerDebug,
    enableDataTableDebugAll,
    disableDataTableDebugAll,
    addDedicatedDataTableDebug,
    removeDedicatedDataTableDebug,
    enableDedicatedDataTableDebug,
    disableDedicatedDataTableDebug,
    enableWebSocketDebug,
    disableWebSocketDebug
} from '~/frontend/src/actions/debugConfig';
import {formatBytes} from '~/shared/src/utilities/valueFormaters';


const DevTools = () => {
    const dispatch    = useDispatch(),
          debugConfig = useSelector( ( state : StoreState ) => state.debugConfig );

    if ( debugConfig?.show ) {
        return <Panel header="YOONITE - DevTools" pt={{ root: { className: 'yn-dev-tools' } }}>
            <label
                key="memoryConsumption"
                className="yn-menu-title-label block"
            >
                Memory consumption
            </label>
            <div
                key="showMemoryConsumption"
                className="yn-dev-tools-switch"
            >
                <InputSwitch
                    inputId="debugShowMemoryConsumptionButton"
                    checked={debugConfig?.memConsumption?.show ?? false}
                    onChange={( event : InputSwitchChangeEvent ) => {
                        if ( event.value ) {
                            dispatch( showMemoryConsumption() );
                        }
                        else {
                            dispatch( hideMemoryConsumption() );
                        }
                    }}
                />
                <label htmlFor="debugShowMemoryConsumptionButton">
                    Show
                </label>
            </div>
            {debugConfig?.memConsumption?.show &&
                <>
                    <hr className="mt-2 mb-1 yn-ruler"/>
                    <div className="yn-dev-tools-info mt-2" style={{ fontSize: '1.2rem' }}>
                        <div>global.data</div>
                        <div style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatBytes( debugConfig.memConsumption?.globalData ?? 0, 1, 1024 )}</div>
                    </div>
                    <div className="yn-dev-tools-info mt-1" style={{ fontSize: '1rem' }}>
                        <div className="text-color-secondary">&nbsp;&nbsp;current panel</div>
                        <div className="text-color-secondary" style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatBytes( debugConfig.memConsumption?.currentPanel?.globalData ?? 0, 1, 1024 )}</div>
                    </div>
                    <hr className="mt-2 mb-1 yn-ruler"/>
                    <div className="yn-dev-tools-info mt-2" style={{ fontSize: '1.2rem' }}>
                        <div>redux store</div>
                        <div style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatBytes( debugConfig.memConsumption?.reduxStore ?? 0, 1, 1024 )}</div>
                    </div>
                    <div className="yn-dev-tools-info mt-1" style={{ fontSize: '1rem' }}>
                        <div className="text-color-secondary">&nbsp;&nbsp;session</div>
                        <div className="text-color-secondary" style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatBytes( debugConfig.memConsumption?.session ?? 0, 1, 1024 )}</div>
                    </div>
                    <div className="yn-dev-tools-info mt-1" style={{ fontSize: '1rem' }}>
                        <div className="text-color-secondary">&nbsp;&nbsp;panelManager</div>
                        <div className="text-color-secondary" style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatBytes( debugConfig.memConsumption?.panelManager ?? 0, 1, 1024 )}</div>
                    </div>
                    <div className="yn-dev-tools-info mt-1" style={{ fontSize: '1rem' }}>
                        <div className="text-color-secondary">&nbsp;&nbsp;elusiveData</div>
                        <div className="text-color-secondary" style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatBytes( debugConfig.memConsumption?.elusiveData ?? 0, 1, 1024 )}</div>
                    </div>
                    <div className="yn-dev-tools-info mt-1" style={{ fontSize: '1rem' }}>
                        <div className="text-color-secondary">&nbsp;&nbsp;current panel</div>
                        <div className="text-color-secondary" style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatBytes( debugConfig.memConsumption?.currentPanel?.reduxTotal ?? 0, 1, 1024 )}</div>
                    </div>
                    <div className="yn-dev-tools-info" style={{ fontSize: '1rem' }}>
                        <div className="text-color-secondary">&nbsp;&nbsp;&nbsp;&nbsp;panelManager</div>
                        <div className="text-color-secondary" style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatBytes( debugConfig.memConsumption?.currentPanel?.panelManager ?? 0, 1, 1024 )}</div>
                    </div>
                    <div className="yn-dev-tools-info" style={{ fontSize: '1rem' }}>
                        <div className="text-color-secondary">&nbsp;&nbsp;&nbsp;&nbsp;elusiveData</div>
                        <div className="text-color-secondary" style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatBytes( debugConfig.memConsumption?.currentPanel?.elusiveData ?? 0, 1, 1024 )}</div>
                    </div>
                </>
            }

            <label
                key="formDebug"
                className="yn-menu-title-label block mt-3"
            >
                Form debug
            </label>
            <div
                key="enableFormDebugAll"
                className="yn-dev-tools-switch"
            >
                <InputSwitch
                    inputId="debugEnableFormDebugAllButton"
                    checked={debugConfig?.formDebug?.showAll ?? false}
                    onChange={( event : InputSwitchChangeEvent ) => {
                        if ( event.value ) {
                            dispatch( enableFormDebugAll() );
                        }
                        else {
                            dispatch( disableFormDebugAll() );
                        }
                    }}
                />
                <label htmlFor="debugEnableFormDebugAllButton">
                    Enable for all forms
                </label>
            </div>
            {!debugConfig?.formDebug?.showAll &&
                <>
                    <label
                        key="dedicatedFormDebug"
                        className="yn-menu-subtitle-label block mt-3"
                    >
                        Dedicated forms
                    </label>
                    {( Object.keys( debugConfig?.formDebug?.dedicated ?? {} ) as string[] ).map(
                        ( formUid ) => {
                            return <div
                                key={`enableDedicatedFormDebug_${formUid}`}
                                className="yn-dev-tools-switch"
                            >
                                <InputSwitch
                                    inputId={`enableDedicatedFormDebugButton_${formUid}`}
                                    checked={debugConfig.formDebug!.dedicated![ formUid ]}
                                    onChange={( event : InputSwitchChangeEvent ) => {
                                        if ( event.value ) {
                                            dispatch( enableDedicatedFormDebug( formUid ) );
                                        }
                                        else {
                                            dispatch( disableDedicatedFormDebug( formUid ) );
                                        }
                                    }}
                                />
                                <InputText
                                    className="ml-2"
                                    value={formUid}
                                    disabled
                                />
                                <Button
                                    onClick={() => {
                                        dispatch( removeDedicatedFormDebug( formUid ) );
                                    }}
                                    icon="pi pi-times"
                                    className="p-button-rounded p-button-danger p-button-text"
                                />
                            </div>
                        }
                    )}
                    <div
                        key="addDedicatedFormDebug"
                        className="yn-dev-tools-switch mt-2"
                    >
                        <InputSwitch
                            checked={false}
                            style={{ visibility: 'hidden' }}
                        />
                        <InputText
                            id="addDedicatedFormDebugInput"
                            className="ml-2"
                            placeholder="formUid"
                        />
                        <Button
                            onClick={() => {
                                const inputEl = document.getElementById( 'addDedicatedFormDebugInput' ) as HTMLInputElement | null;

                                if ( inputEl?.value && !( Object.keys( debugConfig.formDebug?.dedicated ?? {} ) as string[] ).includes( inputEl.value ) ) {
                                    dispatch( addDedicatedFormDebug( inputEl.value ) );
                                }
                            }}
                            icon="fas fa-plus"
                            className="p-button-rounded p-button-success p-button-text"
                        />
                    </div>
                </>
            }

            <label
                key="formDesignerDebug"
                className="yn-menu-title-label block mt-3"
            >
                FormDesigner debug
            </label>
            <div
                key="enableFormDesignerDebugAll"
                className="yn-dev-tools-switch"
            >
                <InputSwitch
                    inputId="debugEnableFormDesignerDebugAllButton"
                    checked={debugConfig?.formDesignerDebug?.showAll ?? false}
                    onChange={( event : InputSwitchChangeEvent ) => {
                        if ( event.value ) {
                            dispatch( enableFormDesignerDebugAll() );
                        }
                        else {
                            dispatch( disableFormDesignerDebugAll() );
                        }
                    }}
                />
                <label htmlFor="debugEnableFormDesignerDebugAllButton">
                    Enable for all form designers
                </label>
            </div>
            {!debugConfig?.formDesignerDebug?.showAll &&
                <>
                    <label
                        key="dedicatedFormDesignerDebug"
                        className="yn-menu-subtitle-label block mt-3"
                    >
                        Dedicated form designer
                    </label>
                    {( Object.keys( debugConfig?.formDesignerDebug?.dedicated ?? {} ) as string[] ).map(
                        ( formUid ) => {
                            return <div
                                key={`enableDedicatedFormDesignerDebug_${formUid}`}
                                className="yn-dev-tools-switch"
                            >
                                <InputSwitch
                                    inputId={`enableDedicatedFormDesignerDebugButton_${formUid}`}
                                    checked={debugConfig.formDesignerDebug!.dedicated![ formUid ]}
                                    onChange={( event : InputSwitchChangeEvent ) => {
                                        if ( event.value ) {
                                            dispatch( enableDedicatedFormDesignerDebug( formUid ) );
                                        }
                                        else {
                                            dispatch( disableDedicatedFormDesignerDebug( formUid ) );
                                        }
                                    }}
                                />
                                <InputText
                                    className="ml-2"
                                    value={formUid}
                                    disabled
                                />
                                <Button
                                    onClick={() => {
                                        dispatch( removeDedicatedFormDesignerDebug( formUid ) );
                                    }}
                                    icon="pi pi-times"
                                    className="p-button-rounded p-button-danger p-button-text"
                                />
                            </div>
                        }
                    )}
                    <div
                        key="addDedicatedFormDesignerDebug"
                        className="yn-dev-tools-switch mt-2"
                    >
                        <InputSwitch
                            checked={false}
                            style={{ visibility: 'hidden' }}
                        />
                        <InputText
                            id="addDedicatedFormDesignerDebugInput"
                            className="ml-2"
                            placeholder="formDesignerUid"
                        />
                        <Button
                            onClick={() => {
                                const inputEl = document.getElementById( 'addDedicatedFormDesignerDebugInput' ) as HTMLInputElement | null;

                                if ( inputEl?.value && !( Object.keys( debugConfig.formDesignerDebug?.dedicated ?? {} ) as string[] ).includes( inputEl.value ) ) {
                                    dispatch( addDedicatedFormDesignerDebug( inputEl.value ) );
                                }
                            }}
                            icon="fas fa-plus"
                            className="p-button-rounded p-button-success p-button-text"
                        />
                    </div>
                </>
            }

            <label
                key="dataTableDebug"
                className="yn-menu-title-label block mt-3"
            >
                DataTable debug
            </label>
            <div
                key="enableDataTableDebugAll"
                className="yn-dev-tools-switch"
            >
                <InputSwitch
                    inputId="debugEnableDataTableDebugAllButton"
                    checked={debugConfig?.dataTableDebug?.showAll ?? false}
                    onChange={( event : InputSwitchChangeEvent ) => {
                        if ( event.value ) {
                            dispatch( enableDataTableDebugAll() );
                        }
                        else {
                            dispatch( disableDataTableDebugAll() );
                        }
                    }}
                />
                <label htmlFor="debugEnableDataTableDebugAllButton">
                    Enable for all dataTables
                </label>
            </div>
            {!debugConfig?.dataTableDebug?.showAll &&
                <>
                    <label
                        key="dedicatedDataTableDebug"
                        className="yn-menu-subtitle-label block mt-3"
                    >
                        Dedicated dataTables
                    </label>
                    {( Object.keys( debugConfig?.dataTableDebug?.dedicated ?? {} ) as string[] ).map(
                        ( dataTableUid ) => {
                            return <div
                                key={`enableDedicatedDataTableDebug_${dataTableUid}`}
                                className="yn-dev-tools-switch"
                            >
                                <InputSwitch
                                    inputId={`enableDedicatedDataTableDebugButton_${dataTableUid}`}
                                    checked={debugConfig.dataTableDebug!.dedicated![ dataTableUid ]}
                                    onChange={( event : InputSwitchChangeEvent ) => {
                                        if ( event.value ) {
                                            dispatch( enableDedicatedDataTableDebug( dataTableUid ) );
                                        }
                                        else {
                                            dispatch( disableDedicatedDataTableDebug( dataTableUid ) );
                                        }
                                    }}
                                />
                                <InputText
                                    className="ml-2"
                                    value={dataTableUid}
                                    disabled
                                />
                                <Button
                                    onClick={() => {
                                        dispatch( removeDedicatedDataTableDebug( dataTableUid ) );
                                    }}
                                    icon="pi pi-times"
                                    className="p-button-rounded p-button-danger p-button-text"
                                />
                            </div>
                        }
                    )}
                    <div
                        key="addDedicatedDataTableDebug"
                        className="yn-dev-tools-switch mt-2"
                    >
                        <InputSwitch
                            checked={false}
                            style={{ visibility: 'hidden' }}
                        />
                        <InputText
                            id="addDedicatedDataTableDebugInput"
                            className="ml-2"
                            placeholder="dataTableUid"
                        />
                        <Button
                            onClick={() => {
                                const inputEl = document.getElementById( 'addDedicatedDataTableDebugInput' ) as HTMLInputElement | null;

                                if ( inputEl?.value && !( Object.keys( debugConfig.dataTableDebug?.dedicated ?? {} ) as string[] ).includes( inputEl.value ) ) {
                                    dispatch( addDedicatedDataTableDebug( inputEl.value ) );
                                }
                            }}
                            icon="fas fa-plus"
                            className="p-button-rounded p-button-success p-button-text"
                        />
                    </div>
                </>
            }

            <label
                key="webSocketDebug"
                className="yn-menu-title-label block mt-3"
            >
                WebSocket debug
            </label>
            <div
                key="enableWebSocketDebug"
                className="yn-dev-tools-switch"
            >
                <InputSwitch
                    inputId="enableWebSocketDebugButton"
                    checked={debugConfig?.webSocketDebug ?? false}
                    onChange={( event : InputSwitchChangeEvent ) => {
                        if ( event.value ) {
                            dispatch( enableWebSocketDebug() );
                        }
                        else {
                            dispatch( disableWebSocketDebug() );
                        }
                    }}
                />
                <label htmlFor="debugEnableDataTableDebugAllButton">
                    Enable
                </label>
            </div>

            <div
                key="bottomSpacer"
                className="mt-2"
            >
                &nbsp;
            </div>
        </Panel>;
    }
    else {
        return null;
    }
};

export default DevTools;
